import {
    ConfigurableProductOptions,
    ConfigurableProductOptionsValues,
    ConfigurableVariant,
    Maybe,
} from '__generated__/__types__';
import {isEqual, isEmpty, intersection} from 'lodash';

export interface SelectedAttributes {
    [key: string]: ConfigurableProductOptionsValues;
}

export function isVariationSelected(productOptions: ConfigurableProductOptions[], attributes: SelectedAttributes) {
    if (isEmpty(productOptions)) return true;
    if (!isEmpty(attributes)) {
        const selectedOptions = [];
        productOptions?.forEach(option => {
            const attributeCode = option.attribute_code;
            if (!attributeCode) {
                return;
            }
            if (Object.hasOwn(attributes, attributeCode)) {
                selectedOptions.push(attributes[attributeCode]);
            }
        });
        return selectedOptions.length === productOptions?.length;
    }
    return false;
}

export const getSelectedVariant = (
    variants: ConfigurableVariant[],
    attributes: SelectedAttributes,
): ConfigurableVariant | undefined => {
    if (isEmpty(variants)) return undefined;
    const selectedVariation = variants?.find(option => {
        const selectedAttributes = [];
        option?.attributes?.forEach(attribute => {
            const attributeCode = attribute?.code;
            if (!attributeCode) {
                return;
            }
            const optionValue = attributes[attributeCode];
            if (isEqual(optionValue?.value_index, attribute?.value_index)) {
                selectedAttributes.push(optionValue);
            }
        });
        return selectedAttributes.length >= option?.attributes?.length!;
    });
    return selectedVariation;
};

export const generateVariations = (productOptions: Maybe<ConfigurableProductOptions>[]) => {
    const validProductOptions = productOptions.filter(option => !!option) as ConfigurableProductOptions[];
    const variations: {[key: string]: ConfigurableProductOptions} = {};

    validProductOptions.forEach(option => {
        const attributeCode = option.attribute_code;
        if (!attributeCode) {
            return;
        }
        if (!variations[attributeCode] && option) {
            variations[attributeCode] = option;
        }
    });

    return variations;
};

export const generateDisabledAttributes = (
    attributes: SelectedAttributes,
    productOptions: ConfigurableProductOptions[],
    variants: ConfigurableVariant[],
) => {
    const disabledAttributes: {[key: string]: Set<number>} = {};
    const variations = generateVariations(productOptions);
    const variationKeys = Object.keys(variations);
    const attributeKeys = Object.keys(attributes);

    // disabled all attributes if matched the conditions
    if (variationKeys.length === 1 || attributeKeys.length > 0) {
        variationKeys.forEach(key => {
            disabledAttributes[key] = new Set<number>();
            variations[key].values?.forEach(value => {
                if (value?.value_index) {
                    disabledAttributes[key].add(value.value_index);
                }
            });
        });
    }

    // if there only one attribute, enable the options which have variants and return
    if (variationKeys.length === 1) {
        const disableAttribute = disabledAttributes[variationKeys[0]];
        variants.forEach(variant => {
            const enabledValueIndex = variant.attributes?.at(0)?.value_index;
            enabledValueIndex && disableAttribute.delete(enabledValueIndex);
        });
        return disabledAttributes;
    }

    const enabledValueIndexMap: {[key: string]: Set<number>} = {};

    attributeKeys.forEach(key => {
        enabledValueIndexMap[key] = new Set<number>(disabledAttributes[key]);
        const selectedValueIndex = attributes[key].value_index;

        variants.forEach(variant => {
            const variantAttributeValueIndex = new Set(variant.attributes?.map(attribute => attribute?.value_index));
            if (!variantAttributeValueIndex.has(selectedValueIndex)) {
                return;
            }
            variant.attributes?.forEach(attribute => {
                if (!attribute?.value_index) {
                    return;
                }
                enabledValueIndexMap[key].add(attribute.value_index);
            });
        });
    });

    const valueIndexSets = Object.values(enabledValueIndexMap);
    const valueIndexArrays = valueIndexSets.map(set => Array.from(set));
    const enabledValueIndexSet = new Set(intersection(...valueIndexArrays));

    variationKeys.forEach(key => {
        disabledAttributes[key]?.forEach(valueIndex => {
            if (enabledValueIndexSet.has(valueIndex)) {
                disabledAttributes[key].delete(valueIndex);
            }
        });
    });

    return disabledAttributes;
};

export const getUnselectedOptions = (productOptions: ConfigurableProductOptions[], attributes: SelectedAttributes) => {
    const selectedOptions = Object.keys(attributes);
    const unselectedOptions = productOptions.filter(option => !selectedOptions.includes(option.attribute_code ?? ''));

    return unselectedOptions;
};
